import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';

const Chat = () => {
  const [message, setMessage] = useState('');
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [room, setRoom] = useState('everyone');
  const [userList, setUserList] = useState([]);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Retrieve user info from localStorage
  const sender = localStorage.getItem('username') || 'Anonymous';
  const userId = localStorage.getItem('userId') || 'guest';
  const role = localStorage.getItem('role') || 'student';

  // Utility function: generate a common room id for one-to-one chat
  const generateCommonRoom = (id1, id2) => {
    return [id1, id2].sort().join('_');
  };

  // Compute the actual room id for chat: if not "everyone", generate a common room id
  const computedRoom = room === 'everyone' ? 'everyone' : generateCommonRoom(userId, room);

  // Scroll to the bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  // Fetch list of users: if role is student, list teachers; if teacher, list students
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const endpoint = role === 'student' ? '/api/teachers' : '/api/students';
        const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`);
        setUserList(res.data);
      } catch (err) {
        console.error('Error fetching users', err);
      }
    };
    fetchUsers();
  }, [role]);

  // Whenever the computedRoom changes, (re)connect to the socket and join the room
  useEffect(() => {
    // Disconnect previous socket if exists
    if (socketRef.current) socketRef.current.disconnect();

    socketRef.current = io(`${process.env.REACT_APP_API_URL}`);
    // Join the computed room
    socketRef.current.emit('joinRoom', computedRoom);

    // Fetch chat history for the room
    const fetchHistory = async () => {
      console.log('Fetching chat history for room:', computedRoom);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/chat/history/${computedRoom}`);
        setChat(res.data);
      } catch (err) {
        console.error('Error fetching chat history', err);
      }
    };
    fetchHistory();

    // Listen for incoming messages
    socketRef.current.on('receiveMessage', (msg) => {
      setChat((prev) => [...prev, msg]);
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, [computedRoom]);

  // Find the receiver's username from userList using the receiver's id
  const findReceiverUsername = (receiverId) => {
    const receiverUser = userList.find((user) => user._id === receiverId);
    return receiverUser ? receiverUser.username : 'Unknown';
  };

  const sendMessage = () => {
    if (message.trim() === '') return;
    setLoading(true);

    // Determine the receiver's name: if "everyone", then use "Everyone"
    const receiver = room === 'everyone' ? 'Everyone' : findReceiverUsername(room);

    const messageObj = {
      room: computedRoom, 
      sender,
      receiver,
      text: message,
    };

    console.log('Sending message:', messageObj);
    socketRef.current.emit('sendMessage', messageObj);
    setMessage('');
    setLoading(false);
  };

  const handleRoomChange = (event) => {
    setRoom(event.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getCurrentChatName = () => {
    if (room === 'everyone') return 'Everyone';
    const user = userList.find(user => user._id === room);
    return user ? user.username : 'Select Chat';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="chat-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .chat-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .chat-container::before {
          content: '';
          position: absolute;
          top: 10%;
          right: 10%;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 4s ease-in-out infinite;
        }

        .chat-container::after {
          content: '';
          position: absolute;
          bottom: 15%;
          left: 8%;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(10deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes messageSlide {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pulse-element {
          position: absolute;
          top: 60%;
          right: 20%;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: pulse 2s ease-in-out infinite;
        }

        .chat-wrapper {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 2rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .chat-main {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.2);
          animation: slideIn 0.6s ease-out 0.3s both;
        }

        .chat-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 1.5rem 2rem;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .chat-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .chat-header-content {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .chat-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .chat-icon {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .chat-name {
          font-size: 1.25rem;
          font-weight: 600;
        }

        .room-selector {
          position: relative;
          min-width: 200px;
        }

        .room-select {
          width: 100%;
          padding: 0.75rem 1rem;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          color: white;
          font-size: 0.9rem;
          font-weight: 500;
          backdrop-filter: blur(10px);
          outline: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .room-select:focus {
          background: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.5);
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
        }

        .room-select option {
          background: #667eea;
          color: white;
          padding: 0.5rem;
        }

        .messages-container {
          height: 400px;
          overflow-y: auto;
          padding: 1.5rem;
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .messages-container::-webkit-scrollbar {
          width: 6px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.05);
          border-radius: 3px;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: rgba(102, 126, 234, 0.3);
          border-radius: 3px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: rgba(102, 126, 234, 0.5);
        }

        .message-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          animation: messageSlide 0.4s ease-out;
        }

        .message-avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          margin-right: 1rem;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .avatar-bot {
          background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
        }

        .avatar-user {
          background: linear-gradient(45deg, #667eea, #764ba2);
        }

        .message-content {
          flex: 1;
          max-width: 75%;
        }

        .message-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .message-sender {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.9rem;
        }

        .message-time {
          font-size: 0.75rem;
          color: #6b7280;
          background: rgba(107, 114, 128, 0.1);
          padding: 0.25rem 0.5rem;
          border-radius: 8px;
        }

        .message-bubble {
          background: white;
          padding: 1rem 1.25rem;
          border-radius: 18px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          border: 1px solid rgba(0,0,0,0.05);
          position: relative;
          word-wrap: break-word;
          line-height: 1.5;
          color: #374151;
        }

        .message-bubble::before {
          content: '';
          position: absolute;
          top: 8px;
          left: -6px;
          width: 12px;
          height: 12px;
          background: white;
          border: 1px solid rgba(0,0,0,0.05);
          border-right: none;
          border-bottom: none;
          transform: rotate(-45deg);
        }

        .bot-message .message-bubble {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-color: rgba(14, 165, 233, 0.1);
        }

        .bot-message .message-bubble::before {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-color: rgba(14, 165, 233, 0.1);
        }

        .empty-state {
          text-align: center;
          padding: 3rem 1rem;
          color: #6b7280;
        }

        .empty-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 70%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          color: white;
        }

        .empty-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #4b5563;
        }

        .empty-description {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .input-container {
          padding: 1.5rem 2rem;
          background: white;
          border-top: 1px solid rgba(0,0,0,0.05);
        }

        .input-wrapper {
          display: flex;
          gap: 1rem;
          align-items: flex-end;
        }

        .message-input {
          flex: 1;
          min-height: 44px;
          max-height: 120px;
          padding: 0.75rem 1rem;
          border: 2px solid rgba(102, 126, 234, 0.2);
          border-radius: 12px;
          font-family: inherit;
          font-size: 0.95rem;
          resize: none;
          outline: none;
          transition: all 0.3s ease;
          background: rgba(248, 250, 252, 0.8);
          backdrop-filter: blur(10px);
        }

        .message-input:focus {
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .message-input::placeholder {
          color: #9ca3af;
        }

        .send-button {
          width: 44px;
          height: 44px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border: none;
          border-radius: 12px;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .send-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        }

        .loading-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          margin: 1rem 0;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 12px;
          color: #667eea;
          font-size: 0.85rem;
          font-style: italic;
        }

        .typing-dots {
          display: flex;
          gap: 2px;
        }

        .typing-dot {
          width: 4px;
          height: 4px;
          background: #667eea;
          border-radius: 50%;
          animation: typingDots 1.4s ease-in-out infinite;
        }

        .typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-dot:nth-child(3) { animation-delay: 0.4s; }

        @keyframes typingDots {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-8px); }
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .chat-container {
            padding: 1rem 0.5rem;
          }
          
          .chat-header {
            padding: 1rem 1.5rem;
          }
          
          .chat-header-content {
            flex-direction: column;
            align-items: stretch;
          }
          
          .room-selector {
            min-width: unset;
            width: 100%;
          }
          
          .messages-container {
            height: 350px;
            padding: 1rem;
          }
          
          .input-container {
            padding: 1rem 1.5rem;
          }
          
          .message-content {
            max-width: 85%;
          }
          
          .main-title {
            font-size: 2rem;
          }
        }

        @media (max-width: 480px) {
          .messages-container {
            height: 300px;
          }
          
          .message-avatar {
            width: 32px;
            height: 32px;
            margin-right: 0.75rem;
            font-size: 0.8rem;
          }
          
          .message-content {
            max-width: 90%;
          }
          
          .input-wrapper {
            gap: 0.75rem;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="chat-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.88,14.42L2,22L9.58,21.12C10.45,21.25 11.23,21.31 12,21.31C17.5,21.31 22,17.73 22,12.31C22,6.58 17.5,3 12,3M12,5C16.42,5 20,7.9 20,11.5C20,15.1 16.42,18 12,18C11.28,18 10.56,17.91 9.86,17.74L6,19L7.26,15.14C5.96,14 5,12.84 5,11.5C5,7.9 8.58,5 12,5Z"/>
            </svg>
            Real-time Communication
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Live</span> Chat
          </h1>
          
          <p className="subtitle">
            Connect and communicate with teachers and students instantly
          </p>
        </div>

        {/* Chat Main Container */}
        <div className="chat-main">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-title">
                <div className="chat-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.88,14.42L2,22L9.58,21.12C10.45,21.25 11.23,21.31 12,21.31C17.5,21.31 22,17.73 22,12.31C22,6.58 17.5,3 12,3Z"/>
                  </svg>
                </div>
                <span className="chat-name">Chat with {getCurrentChatName()}</span>
              </div>
              
              <div className="room-selector">
                <select 
                  className="room-select"
                  value={room}
                  onChange={handleRoomChange}
                >
                  <option value="everyone">Everyone</option>
                  {userList.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Messages Container */}
          <div className="messages-container">
            {chat.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.88,14.42L2,22L9.58,21.12C10.45,21.25 11.23,21.31 12,21.31C17.5,21.31 22,17.73 22,12.31C22,6.58 17.5,3 12,3Z"/>
                  </svg>
                </div>
                <div className="empty-title">No messages yet</div>
                <div className="empty-description">Start the conversation by sending a message!</div>
              </div>
            ) : (
              <>
                {chat.map((msg, index) => (
                  <div key={index} className={`message-item ${msg.sender === 'Anonymous' ? 'bot-message' : ''}`}>
                    <div className={`message-avatar ${msg.sender === 'Anonymous' ? 'avatar-bot' : 'avatar-user'}`}>
                      {msg.sender === 'Anonymous' ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.75,4.09L15.22,6.03L16.13,9.09L13.5,7.28L10.87,9.09L11.78,6.03L9.25,4.09L12.44,4L13.5,1L14.56,4L17.75,4.09M21.25,11L19.61,12.25L20.2,14.23L18.5,13.06L16.8,14.23L17.39,12.25L15.75,11L17.81,10.95L18.5,9L19.19,10.95L21.25,11M18.97,15.95C19.8,15.87 20.69,17.05 20.16,17.8C19.84,18.25 19.5,18.67 19.08,19.07C15.17,23 8.84,23 4.94,19.07C1.03,15.17 1.03,8.83 4.94,4.93C5.34,4.53 5.76,4.17 6.21,3.85C6.96,3.32 8.14,4.21 8.06,5.04C7.79,7.9 8.75,10.87 10.95,13.06C13.14,15.26 16.1,16.22 18.97,15.95M17.33,17.97C14.5,17.81 11.7,16.64 9.53,14.5C7.36,12.31 6.2,9.5 6.04,6.68C3.23,9.82 3.34,14.4 6.35,17.41C9.37,20.43 14,20.54 17.33,17.97Z"/>
                        </svg>
                      ) : (
                        msg.sender.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <span className="message-sender">{msg.sender}</span>
                        <span className="message-time">{formatTime(msg.timestamp || new Date())}</span>
                      </div>
                      <div className="message-bubble">
                        {msg.text}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="typing-indicator">
                    <span>Sending message</span>
                    <div className="typing-dots">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Container */}
          <div className="input-container">
            <div className="input-wrapper">
              <textarea
                className="message-input"
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows="1"
                style={{
                  height: 'auto',
                  minHeight: '44px',
                  maxHeight: '120px',
                  overflowY: message.length > 100 ? 'auto' : 'hidden'
                }}
              />
              <button
                className="send-button"
                onClick={sendMessage}
                disabled={loading || !message.trim()}
              >
                {loading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;